import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import SetupForm from "./SetupForm";
import { getStoreId } from "../../Shared/AppContext/selectors";
import { checkHttpStatus } from "../../Shared/utility";
import { useState } from "react";
import queryString from "query-string";

// Bump this value when we need an easy runtime marker for deployed checkout JS.
const STRIPE_SETUP_INTENT_CLIENT_REV = "2026-03-18.1";

export default function AddCard({
  stripeCustomerId,
  closeModal,
  setCreditCardsList,
  currentTab,
  setAchData,
  setCurrentCardNumber,
  setCurrentAccNumber,
  creditCardsList,
  achAccountList,
  setCardBrandName,
  setAddNewCartData,
  setAddNewACHAccountData,
  setErrorMsgPlaceOrder,
  setErrorMsg,
  canAddNewCard,
  canAddNewACH,
  currentFileRequired,
  translationKey,
}) {
  const storeId = useSelector(getStoreId);
  const [clientSecretKey, setClientSecretkey] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState("");
  const paymentMethodNotAvailableMessage = translationKey(
    currentFileRequired["PAYMENT_METHOD_NOT_AVAILABLE_MESSAGE"]
  );
  const paymentSetupInitFailedMessage = translationKey(
    currentFileRequired["PAYMENT_SETUP_INIT_FAILED_MESSAGE"]
  );

  //https://localhost/wcs/resources/store/10051/stripe/createSetupIntent?customerId=cus_NvQxkjchlBbmuw1

  useEffect(() => {
    let isMounted = true;

    async function initStripeSetup() {
      if (!storeId) {
        return;
      }

      const canCreateForCurrentTab =
        currentTab === "card"
          ? canAddNewCard
          : currentTab === "ach"
            ? canAddNewACH
            : false;

      if (!canCreateForCurrentTab) {
        setErrorMsg(paymentMethodNotAvailableMessage);
        setInitError(paymentMethodNotAvailableMessage);
        return;
      }

      setIsInitializing(true);
      setInitError("");
      setClientSecretkey("");
      setStripePromise(null);

      const token = document.getElementById("reactWCToken")?.value;
      const trimmedCustomerId =
        typeof stripeCustomerId === "string" ? stripeCustomerId.trim() : "";
      const queryParams = {
        paymentMethodType: currentTab === "card" ? "card" : "us_bank_account",
      };
      if (trimmedCustomerId) {
        queryParams.customerId = trimmedCustomerId;
      }
      const query = queryString.stringify(queryParams);

      try {
        const [setupIntentResponse, publicKeyResponse] = await Promise.all([
          fetch(
            `/wcs/resources/store/${storeId}/stripe/createSetupIntent?${query}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Checkout-Client-Rev": STRIPE_SETUP_INTENT_CLIENT_REV,
                WCAuthToken: token
                  ? token
                  : "331732%2C4cz6xGvFnASx0JELVXW0E3GFFeFtzozCzHY8u16FNEE%3D",
              },
            }
          ),
          fetch(`/wcs/resources/store/${storeId}/stripe/getPublicKey`, {
            method: "GET",
            credentials: "same-origin",
            cache: "no-cache",
          }),
        ]);

        const setupIntentInfo = await checkHttpStatus(setupIntentResponse);
        if (!setupIntentInfo?.clientSecretKey) {
          throw new Error("Missing Stripe setup intent client secret");
        }

        if (publicKeyResponse.status !== 200) {
          throw new Error(
            `Unable to fetch Stripe public key (status ${publicKeyResponse.status})`
          );
        }
        const publicKeyResponseData = await publicKeyResponse.json();
        const publicKey =
          typeof publicKeyResponseData?.stripePublicKey === "string"
            ? publicKeyResponseData.stripePublicKey.trim()
            : "";
        if (!publicKey) {
          throw new Error("Stripe public key is empty");
        }

        const stripeInstance = await loadStripe(publicKey, {
          betas: ["blocked_card_brands_beta_2"],
        });
        if (!stripeInstance) {
          throw new Error("Stripe failed to initialize");
        }

        if (isMounted) {
          setClientSecretkey(setupIntentInfo.clientSecretKey);
          setStripePromise(stripeInstance);
          setErrorMsg("");
        }
      } catch (error) {
        console.error("CHECKOUT_STRIPE_INIT_ERROR", {
          storeId,
          currentTab,
          hasCustomerId: !!trimmedCustomerId,
          message: error?.message || "Unknown Stripe init error",
        });
        if (isMounted) {
          setInitError(paymentSetupInitFailedMessage);
          setErrorMsg(paymentSetupInitFailedMessage);
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    }

    initStripeSetup();
    return () => {
      isMounted = false;
    };
  }, [
    storeId,
    stripeCustomerId,
    currentTab,
    canAddNewCard,
    canAddNewACH,
    closeModal,
    paymentMethodNotAvailableMessage,
    paymentSetupInitFailedMessage,
    setErrorMsg,
  ]);

  const options = {
    appearance: {
      theme: "stripe",
    },
    disallowedCardBrands: ["discover_global_network"],
    clientSecret: clientSecretKey,
  };

  if (initError) {
    return (
      <div className="add-card-init-state">
        <p className="checkout-error-message">{initError}</p>
      </div>
    );
  }

  if (isInitializing) {
    return (
      <div className="add-card-init-state">
        <p className="checkout-sub-title">Initializing secure payment form...</p>
      </div>
    );
  }

  if (!clientSecretKey || !stripePromise) {
    return (
      <div className="add-card-init-state">
        <p className="checkout-error-message">
          {paymentSetupInitFailedMessage}
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <SetupForm
        closeModal={closeModal}
        storeId={storeId}
        setCreditCardsList={setCreditCardsList}
        currentTab={currentTab}
        setAchData={setAchData}
        setCurrentCardNumber={setCurrentCardNumber}
        setCurrentAccNumber={setCurrentAccNumber}
        creditCardsList={creditCardsList}
        achAccountList={achAccountList}
        setCardBrandName={setCardBrandName}
        setAddNewCartData={setAddNewCartData}
        setAddNewACHAccountData={setAddNewACHAccountData}
        setErrorMsgPlaceOrder={setErrorMsgPlaceOrder}
        setErrorMsg={setErrorMsg}
      />
    </Elements>
  );
}
